import {
  useQuery as useReactQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { camelCase, snakeCase } from "lodash";
import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
} from "@src/global_functions/postgrestApi";
import request from "@src/global_functions/request";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";

const overviewPrimarySiteContacts = {
  useQuery,
  mutations: {
    usePost,
    useDelete,
  },
} as const;

export default overviewPrimarySiteContacts;

export const siteRoles = [
  "assetManager",
  "propertyManager",
  "assistantPropertyManager",
  "buildingEngineer",
] as const;
export type SiteRole = (typeof siteRoles)[number];

const SITE_ROLE_PRIMARY_CONTACT_KEY = "site-role-primary-contacts";
type SiteRolePrimaryContact = {
  firstName?: string;
  lastName?: string;
  email?: string;
  userId: number;
  siteRole: SiteRole;
  siteId: number;
};
interface SiteRolePrimaryContactRequest {
  // Has precedence over accountId
  siteId?: number;

  accountId?: number;
}
const getSiteRolePrimaryContacts = async (
  req: SiteRolePrimaryContactRequest
): Promise<SiteRolePrimaryContact[] | null> => {
  const query = req.siteId
    ? {
        site_id: `eq.${req.siteId}`,
      }
    : {
        account_id: `eq.${req.accountId}`,
      };

  if (req.accountId || req.siteId)
    return request
      .get(`/rest/v_site_role_primary_contacts_summary`)
      .query(query)
      .then(({ body }) => body.map(recursiveCamelCaseCipher));

  return null;
};
function useQuery(req: SiteRolePrimaryContactRequest) {
  return useReactQuery({
    queryKey: [SITE_ROLE_PRIMARY_CONTACT_KEY, req],
    queryFn: () => getSiteRolePrimaryContacts(req),

    select: (data) =>
      data?.map((contact) => ({
        ...contact,
        siteRole: camelCase(contact.siteRole) as (typeof siteRoles)[number],
      })),

    refetchOnWindowFocus: false,
  });
}

type SiteRolePrimaryContactInput = Pick<
  SiteRolePrimaryContact,
  "siteId" | "siteRole" | "userId"
>;

// endpoint requires {feature: "Building Overview", access: "edit"}
const postSiteRolePrimaryContacts = (
  body: SiteRolePrimaryContactInput,
  {
    checkFeatureAccessHeader,
  }: { checkFeatureAccessHeader: CheckFeatureAccessHeader }
) =>
  request
    .post("/rest/site_role_primary_contacts")
    .set("Prefer", "resolution=merge-duplicates")
    .set(checkFeatureAccessHeader)
    .query({ on_conflict: "site_id,site_role" })
    .send({
      ...recursiveSnakeCaseCipher(body),
      site_role: snakeCase(body.siteRole),
    });
function usePost(siteId: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Building Overview", "edit"],
    (body: SiteRolePrimaryContactInput, checkFeatureAccessHeader) =>
      postSiteRolePrimaryContacts(body, { checkFeatureAccessHeader }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            SITE_ROLE_PRIMARY_CONTACT_KEY,
            {
              siteId,
            },
          ],
        });
      },
    }
  );
}

// endpoint requires {feature: "Building Overview", access: "edit"}
const deleteSiteRolePrimaryContacts = (
  {
    role,
    siteId,
  }: {
    role: (typeof siteRoles)[number];
    siteId: number;
  },
  {
    checkFeatureAccessHeader,
  }: { checkFeatureAccessHeader: CheckFeatureAccessHeader }
) =>
  request
    .delete("/rest/site_role_primary_contacts")
    .set(checkFeatureAccessHeader)
    .query({ site_role: `eq.${snakeCase(role)}`, site_id: `eq.${siteId}` });

function useDelete(siteId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Building Overview", "edit"],
    (role: (typeof siteRoles)[number], checkFeatureAccessHeader) =>
      deleteSiteRolePrimaryContacts(
        { role, siteId },
        { checkFeatureAccessHeader }
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            SITE_ROLE_PRIMARY_CONTACT_KEY,
            {
              siteId,
            },
          ],
        });
      },
    }
  );
}
